import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChartLine,
  faCrown,
  faDollarSign,
  faEye,
  faLink,
  faMapMarkerAlt,
  faMobileScreenButton,
  faSync,
  faUserCheck,
  faUserPlus,
  faUsers,
  faUserXmark,
} from "@fortawesome/free-solid-svg-icons";
import { UserContext } from "../context/user/UserContext";
import { analyticsAPI } from "../../services/analyticsService";
import { BarChart, LineChart } from "../AnalyticsCharts";
import { ChartCard, LeaderList, StatCard } from "../AnalyticsWidgets";
import Loader from "../Loader";
import Styles from "../../styles/Analytics.module.css";
import {
  formatCurrency,
  formatDateTime,
  formatMoney,
  formatNumber,
  percent,
} from "../../utils/analyticsUtils";

const EMPTY_OVERVIEW = {
  summary: {},
  monthly: [],
  screens: [],
  platforms: [],
  top_contributors: [],
  top_affiliates: [],
};

const RANGE_OPTIONS = [
  { key: "12m", label: "Last 12 months", months: 12 },
  { key: "24m", label: "Last 24 months", months: 24 },
  { key: "ytd", label: "Year to date", ytd: true },
  { key: "all", label: "All available data", all: true },
];

const getDateRange = (rangeKey) => {
  const option = RANGE_OPTIONS.find((item) => item.key === rangeKey);
  const end = new Date();
  const start = option.all
    ? new Date(end.getFullYear() - 4, 0, 1)
    : option.ytd
      ? new Date(end.getFullYear(), 0, 1)
      : new Date(end.getFullYear(), end.getMonth() - option.months + 1, 1);

  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
    label: option.label,
  };
};

const toChartData = (rows, key) =>
  rows.map((row) => ({
    label: row.label,
    fullLabel: row.full_label,
    value: Number(row[key]) || 0,
  }));

const screenLabel = (value) =>
  value
    .split(/[._-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const Analytics = () => {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const [rangeKey, setRangeKey] = useState("12m");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(EMPTY_OVERVIEW);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const currentRequest = requestId.current + 1;
    requestId.current = currentRequest;
    const range = getDateRange(rangeKey);
    setLoading(true);
    setError("");
    setData(EMPTY_OVERVIEW);

    try {
      const overview = await analyticsAPI.getOverview({
        startDate: range.startDate,
        endDate: range.endDate,
      });
      if (requestId.current === currentRequest) {
        setData({ ...EMPTY_OVERVIEW, ...(overview || {}) });
      }
    } catch (loadError) {
      if (requestId.current === currentRequest) {
        setError(loadError.message || "Failed to load analytics");
      }
    } finally {
      if (requestId.current === currentRequest) {
        setLoading(false);
      }
    }
  }, [rangeKey]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data.summary || {};
  const range = getDateRange(rangeKey);
  const trialConversion = percent(
    summary.trial_to_paid_users,
    summary.trial_users_lifetime,
  );
  const trialedLeftUsers = Math.max(
    0,
    (Number(summary.trial_users_lifetime) || 0) -
      (Number(summary.trial_to_paid_users) || 0) -
      (Number(summary.current_trial_users) || 0),
  );
  const revenueCurrencies = Object.entries(
    summary.revenue_by_currency || {},
  );

  const signupsSeries = useMemo(
    () => toChartData(data.monthly, "signups"),
    [data.monthly],
  );
  const subscriptionsSeries = useMemo(
    () =>
      data.monthly.map((row) => ({
        label: row.label,
        fullLabel: row.full_label,
        value:
          (Number(row.monthly_subscriptions) || 0) +
          (Number(row.annual_subscriptions) || 0),
      })),
    [data.monthly],
  );
  const appOpensSeries = useMemo(
    () => toChartData(data.monthly, "app_opens"),
    [data.monthly],
  );
  const screenViewsSeries = useMemo(
    () => toChartData(data.monthly, "screen_views"),
    [data.monthly],
  );
  const revenueSeries = useMemo(
    () =>
      data.monthly.map((row) => ({
        label: row.label,
        fullLabel: row.full_label,
        value: Number(row.revenue_by_currency?.USD) || 0,
      })),
    [data.monthly],
  );

  const topContributors = useMemo(
    () =>
      data.top_contributors.map((item) => ({
        id: item.id,
        name: `@${item.username}`,
        meta: item.email,
        value: item.locations,
      })),
    [data.top_contributors],
  );
  const topAffiliates = useMemo(
    () =>
      data.top_affiliates.map((item) => ({
        id: item.id,
        name: `@${item.username}`,
        meta: `${item.email}${item.referral_code ? ` • ${item.referral_code}` : ""}`,
        value: item.referrals,
      })),
    [data.top_affiliates],
  );

  if (loading && !data.generated_at) {
    return (
      <div className={Styles.container}>
        <Loader text="Loading analytics..." />
      </div>
    );
  }

  return (
    <div className={Styles.container}>
      <div className={Styles.headerRow}>
        <div>
          <h1 className={Styles.title}>Business Analytics</h1>
          <p className={Styles.welcomeMessage}>
            Welcome back, {userData?.user?.username || "Admin"}. This dashboard
            covers acquisition, conversion, subscriptions, revenue, referrals,
            and app engagement.
          </p>
        </div>
        <div className={Styles.headerActions}>
          <select
            className={Styles.rangeSelect}
            value={rangeKey}
            onChange={(event) => setRangeKey(event.target.value)}
            aria-label="Analytics date range"
          >
            {RANGE_OPTIONS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={Styles.refreshButton}
            onClick={load}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} />
            {loading ? "Refreshing" : "Refresh"}
          </button>
          <button
            type="button"
            className={Styles.backButton}
            onClick={() => navigate("/admin")}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Dashboard
          </button>
        </div>
      </div>

      {error && (
        <div className={Styles.errorBox} role="alert">
          {error}
        </div>
      )}

      <div className={Styles.metaRow}>
        <span>{range.label}</span>
        <span>Updated {formatDateTime(data.generated_at)}</span>
      </div>

      <div className={Styles.statsGrid}>
        <StatCard
          icon={faUsers}
          label="Registered Users"
          value={formatNumber(summary.total_users)}
          hint={`${formatNumber(summary.new_users)} in this range • ${formatNumber(
            summary.new_users_7d,
          )} this week`}
          color="#3b82f6"
          onClick={() => navigate("/analytics/users")}
        />
        <StatCard
          icon={faMobileScreenButton}
          label="App Installs"
          value={formatNumber(summary.total_installs)}
          hint={`${formatNumber(summary.new_installs)} first opens in this range`}
          color="#0ea5e9"
        />
        <StatCard
          icon={faUserXmark}
          label="Downloaded, Never Bought"
          value={formatNumber(summary.installed_never_subscribed)}
          hint="Installs with no paid subscription"
          color="#ef4444"
          onClick={() => navigate("/analytics/users?filter=inactive_app")}
        />
        <StatCard
          icon={faUserCheck}
          label="Active App Users (30d)"
          value={formatNumber(summary.active_app_users_30d)}
          hint={`${formatNumber(summary.app_opens)} opens in this range`}
          color="#14b8a6"
          onClick={() => navigate("/analytics/users?filter=active_app")}
        />
        <StatCard
          icon={faCrown}
          label="Monthly Subscribers"
          value={formatNumber(summary.monthly_subscribers)}
          hint={`${formatNumber(summary.lifetime_monthly_subscribers)} lifetime`}
          color="#f97316"
          onClick={() => navigate("/analytics/users?filter=monthly")}
        />
        <StatCard
          icon={faCrown}
          label="Annual Subscribers"
          value={formatNumber(summary.annual_subscribers)}
          hint={`${formatNumber(summary.lifetime_annual_subscribers)} lifetime`}
          color="#fbbf24"
          onClick={() => navigate("/analytics/users?filter=annual")}
        />
        <StatCard
          icon={faUserPlus}
          label="Trialed, Never Paid"
          value={formatNumber(trialedLeftUsers)}
          hint={`${formatNumber(summary.trial_to_paid_users)} converted • ${trialConversion}%`}
          color="#8b5cf6"
          onClick={() => navigate("/analytics/users?filter=trialed_left")}
        />
         <StatCard
           icon={faDollarSign}
          label="Gross Subscription Value"
          value={formatCurrency(summary.gross_subscription_value_usd)}
          hint={`${formatCurrency(summary.recorded_revenue_usd)} recorded + ${formatCurrency(
            summary.estimated_revenue_usd,
          )} estimated`}
          color="#10b981"
        />
        <StatCard
          icon={faLink}
          label="Referrals"
          value={formatNumber(summary.total_referrals)}
          hint={`${formatNumber(summary.paid_referrals)} paid • ${formatNumber(
            summary.effective_affiliates,
          )} effective affiliates`}
          color="#f97316"
          onClick={() => navigate("/analytics/referrals")}
        />
        <StatCard
          icon={faMapMarkerAlt}
          label="Locations"
          value={formatNumber(summary.total_locations)}
          hint={`${formatNumber(summary.contributors)} contributors • ${formatNumber(
            summary.reported_locations,
          )} reported`}
          color="#ec4899"
        />
      </div>

      <div className={Styles.sectionHeader}>
        <div>
          <h2 className={Styles.sectionTitle}>Growth and Conversion</h2>
          <p className={Styles.sectionSubtitle}>{range.label}</p>
        </div>
      </div>

      <div className={Styles.chartsGrid}>
        <ChartCard
          title="New Registrations"
          subtitle={range.label}
          total={formatNumber(summary.new_users)}
          totalLabel="users"
        >
          <BarChart
            data={signupsSeries}
            color="#3b82f6"
            ariaLabel="New registrations by month"
          />
        </ChartCard>
        <ChartCard
          title="Paid Subscriptions"
          subtitle="Monthly and annual paid conversion events"
          total={formatNumber(summary.paid_subscribers_lifetime)}
          totalLabel="lifetime"
        >
          <BarChart
            data={subscriptionsSeries}
            color="#10b981"
            ariaLabel="Paid subscriptions by month"
          />
        </ChartCard>
        <ChartCard
          title="Subscription Value (USD)"
          subtitle="Recorded prices plus catalog estimates"
          total={formatCurrency(summary.gross_subscription_value_usd)}
          totalLabel="gross value"
        >
          <BarChart
            data={revenueSeries}
            color="#fbbf24"
            valueFormatter={formatCurrency}
            ariaLabel="Monthly subscription value in US dollars"
          />
        </ChartCard>
        <ChartCard
          title="Referral Usage"
          subtitle="Successful code uses"
          total={formatNumber(summary.range_referrals)}
          totalLabel="referrals"
        >
          <BarChart
            data={toChartData(data.monthly, "referrals")}
            color="#f97316"
            ariaLabel="Referrals by month"
          />
        </ChartCard>
      </div>

      <div className={Styles.sectionHeader}>
        <div>
          <h2 className={Styles.sectionTitle}>App Traffic</h2>
          <p className={Styles.sectionSubtitle}>
            First-open, app-open, and screen-view events begin populating after
            the updated mobile app is released.
          </p>
        </div>
      </div>

      <div className={Styles.chartsGrid}>
        <ChartCard
          title="App Opens"
          subtitle={range.label}
          total={formatNumber(summary.app_opens)}
          totalLabel="opens"
        >
          <LineChart
            data={appOpensSeries}
            color="#0ea5e9"
            ariaLabel="Monthly app opens"
          />
        </ChartCard>
        <ChartCard
          title="Screen Views"
          subtitle={range.label}
          total={formatNumber(summary.screen_views)}
          totalLabel="views"
        >
          <LineChart
            data={screenViewsSeries}
            color="#8b5cf6"
            ariaLabel="Monthly screen views"
          />
        </ChartCard>
      </div>

      <div className={Styles.bottomGrid}>
        <div className={Styles.card}>
          <div className={Styles.cardHeader}>
            <div>
              <h3 className={Styles.cardTitle}>Most Viewed Screens</h3>
              <p className={Styles.cardSubtitle}>Views and unique installs</p>
            </div>
            <FontAwesomeIcon icon={faEye} />
          </div>
          {data.screens.length ? (
            <div className={Styles.tableWrapper}>
              <table className={Styles.table}>
                <thead>
                  <tr>
                    <th>Screen</th>
                    <th>Views</th>
                    <th>Users</th>
                    <th>Per User</th>
                  </tr>
                </thead>
                <tbody>
                  {data.screens.slice(0, 10).map((screen) => (
                    <tr key={screen.screen_name}>
                      <td>{screenLabel(screen.screen_name)}</td>
                      <td>{formatNumber(screen.views)}</td>
                      <td>{formatNumber(screen.users)}</td>
                      <td>{Number(screen.views_per_user || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={Styles.emptyText}>
              Screen analytics will appear after the updated mobile app is used.
            </p>
          )}
        </div>

        <div className={Styles.card}>
          <div className={Styles.cardHeader}>
            <div>
              <h3 className={Styles.cardTitle}>Platform Breakdown</h3>
              <p className={Styles.cardSubtitle}>Installs and event volume</p>
            </div>
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          {data.platforms.length ? (
            <div className={Styles.tableWrapper}>
              <table className={Styles.table}>
                <thead>
                  <tr>
                    <th>Platform</th>
                    <th>Installs</th>
                    <th>Opens</th>
                    <th>Screen Views</th>
                  </tr>
                </thead>
                <tbody>
                  {data.platforms.map((platform) => (
                    <tr key={platform.platform}>
                      <td>{screenLabel(platform.platform)}</td>
                      <td>{formatNumber(platform.installs)}</td>
                      <td>{formatNumber(platform.app_opens)}</td>
                      <td>{formatNumber(platform.screen_views)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={Styles.emptyText}>
              Platform data will appear after the first tracked app session.
            </p>
          )}
        </div>
      </div>

      <div className={Styles.sectionHeader}>
        <div>
          <h2 className={Styles.sectionTitle}>Top Users</h2>
          <p className={Styles.sectionSubtitle}>
            Select a user to open their complete profile, subscriptions, and
            referral network.
          </p>
        </div>
      </div>

      <div className={Styles.leadersGrid}>
        <div className={Styles.card}>
          <div className={Styles.cardHeader}>
            <div>
              <h3 className={Styles.cardTitle}>Top Contributors</h3>
              <p className={Styles.cardSubtitle}>Locations added</p>
            </div>
          </div>
          <LeaderList
            items={topContributors}
            valueLabel="locations"
            emptyText="No contributor data yet."
            onItemClick={(item) => navigate(`/analytics/users/${item.id}`)}
          />
        </div>
        <div className={Styles.card}>
          <div className={Styles.cardHeader}>
            <div>
              <h3 className={Styles.cardTitle}>Top Affiliates</h3>
              <p className={Styles.cardSubtitle}>Code usage</p>
            </div>
          </div>
          <LeaderList
            items={topAffiliates}
            valueLabel="referrals"
            emptyText="No affiliate activity yet."
            onItemClick={(item) => navigate(`/analytics/users/${item.id}`)}
          />
          <Link className={Styles.cardLink} to="/analytics/referrals">
            Open referral analytics →
          </Link>
        </div>
      </div>

      {revenueCurrencies.length > 1 && (
        <p className={Styles.tableNote}>
          Revenue is shown separately by currency:{" "}
          {revenueCurrencies
            .map(
              ([currency, values]) => formatMoney(values.gross, currency),
            )
            .join(" • ")}
          . Store proceeds still require a RevenueCat/App Store Connect finance
          reconciliation.
        </p>
      )}
    </div>
  );
};

export default Analytics;
